import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { Tooltip, TooltipProvider } from '../../admin/components/shared/Tooltip.tsx';
import ProcessGenerateFlowchartPanel from './ProcessGenerateFlowchartPanel';
import { parseGeneratedFlowchartVersion } from './functional';

const DEFAULT_FOOTER_STATE = {
  generateDisabled: true,
  generateDisabledReason: null,
  emptyChartDisabled: true,
  isGenerating: false,
  documentCount: 0,
  selectionCount: 0
};

export function ProcessGenerateFlowchartModal({
  isOpen,
  onClose,
  cycleId,
  processId,
  processName,
  description,
  initialProcess,
  /** True while the parent is still fetching process details (placeholder data in use). */
  isLoading = false,
  onGenerated
}) {
  const titleId = useId();
  const panelRef = useRef(null);
  const [footerState, setFooterState] = useState(DEFAULT_FOOTER_STATE);

  useEffect(() => {
    if (!isOpen) {
      setFooterState(DEFAULT_FOOTER_STATE);
    }
  }, [isOpen]);

  const handleGenerateUiStateChange = useCallback((state) => {
    setFooterState(state);
  }, []);

  const handleGenerateSuccess = useCallback(
    async (data) => {
      const versionNumber = parseGeneratedFlowchartVersion(data);
      await onGenerated?.(versionNumber);
      onClose();
    },
    [onClose, onGenerated]
  );

  const runGenerate = useCallback(
    async (options) => {
      if (panelRef.current?.uploadPendingDocuments) {
        try {
          await panelRef.current.uploadPendingDocuments();
        } catch {
          return;
        }
      }
      await panelRef.current?.generateFlowchart?.(options);
    },
    []
  );

  const handleBackdropClose = useCallback(() => {
    if (footerState.isGenerating) return;
    onClose();
  }, [footerState.isGenerating, onClose]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleBackdropClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleBackdropClose]);

  const headerCountLabel = useMemo(() => {
    if (isLoading) return 'Loading…';
    const total = footerState.documentCount;
    const selected = footerState.selectionCount;
    const totalLabel = total === 1 ? '1 document' : `${total} documents`;
    if (selected > 0) {
      return `${selected} selected · ${totalLabel}`;
    }
    return totalLabel;
  }, [footerState.documentCount, footerState.selectionCount, isLoading]);

  if (!isOpen || typeof document === 'undefined') return null;

  const generateDisabled = isLoading || footerState.generateDisabled;
  const generateDisabledReason = isLoading
    ? 'Loading process details…'
    : footerState.generateDisabledReason;

  const generateButton = (
    <button
      type="button"
      onClick={() => void runGenerate()}
      disabled={generateDisabled}
      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {footerState.isGenerating ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : null}
      Generate
    </button>
  );

  return createPortal(
    <TooltipProvider>
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="absolute inset-0 bg-black/50" aria-hidden="true" onClick={handleBackdropClose} />
        <div
          className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
            <div className="min-w-0">
              <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                Generate flowchart
              </h2>
              {processName ? (
                <p className="mt-0.5 text-sm text-gray-500">{processName}</p>
              ) : null}
            </div>
            <p className="shrink-0 text-sm text-gray-500">{headerCountLabel}</p>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div
                className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-sm text-gray-500"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" aria-hidden="true" />
                <span>Loading process documents…</span>
              </div>
            ) : (
              <ProcessGenerateFlowchartPanel
                key={`${processId ?? ''}`}
                ref={panelRef}
                presentation="content"
                cycleId={cycleId}
                processId={processId}
                processName={processName}
                description={description}
                initialProcess={initialProcess}
                onGenerateSuccess={handleGenerateSuccess}
                onGenerateUiStateChange={handleGenerateUiStateChange}
                overlayZIndex={260}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/80 px-6 py-4">
            <button
              type="button"
              onClick={handleBackdropClose}
              disabled={footerState.isGenerating}
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* <button
                type="button"
                onClick={() => void runGenerate({ emptyChart: true })}
                disabled={footerState.emptyChartDisabled}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Empty chart
              </button> */}
              {generateDisabled && generateDisabledReason ? (
                <Tooltip content={generateDisabledReason} side="top">
                  <span className="inline-flex">{generateButton}</span>
                </Tooltip>
              ) : (
                generateButton
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>,
    document.body
  );
}

export default ProcessGenerateFlowchartModal;
